import React, { useCallback, useMemo, useState, useContext, useEffect } from "react";
import { graphql, useStaticQuery, withPrefix } from "gatsby";
import safeSlufigy from "../utility/safe-slugify";
import classNames from "classnames";
import CardProdotto from "./card-prodotto";
import CustomSelect from "./custom-select";
import CompareContext from "../context/compare";
import useBlockScroll from "../hooks/use-block-scroll";
import useKeepScroll from "../hooks/use-keep-scroll";
import useProductPrices from "../hooks/use-product-prices";
import { productImpression } from "../utility/datalayer";
import DoubleRangeSlider from "./elements/double-range-slider";
import ToggleSwitch from "./elements/toggle-switch";
import Facet from "./elements/facet";
import RotateIcon from "../icons/arrow-rotate-right-light.svg";
import JsonLd from "./json-ld";
import { normalizeRelativePath } from "../utility/paths";
import BannerPromoCM from "./banner-promo-cm";
import BannerPushNotification from "./elements/banner-push-notification";
import { extractValues, productMatchesFilters } from "../utility/filters";
import Filter from "../icons/filter.svg";
import Order from "../icons/ordina-per.svg";
import { hasEcommerce } from "../utility/ecommerce";
import useNoRtl from "../hooks/use-no-rtl";
import { Helmet } from "react-helmet-async";
import useIsMobile from "../hooks/use-is-mobile";
import { useProduct } from "../query/use-product";
import AutocompleteSelectPlp from "./elements/autocomplete-select-plp";

const PRICE_SORT_OPTIONS = ["price_asc", "price_desc", "priority_per_price"];

const ProductListCross = ({
  pageCrossLine,
  products: allProducts,
  config,
  hasHero,
  location,
  bannerPromoCM,
  className,
  categoryModule,
}) => {
  const { pclTitle, pclIntroBefore, pclIntroAfter, order, filterMagento } = pageCrossLine;
  const { productIds, toggleProduct } = useContext(CompareContext);

  const {
    labelManagerLabels,
    site,
    config: { currency },
  } = useStaticQuery(graphql`
    {
      config: liferayGeneralConfiguration {
        haierStEcommLines {
          productLine
        }
        currency
        wtbAndRetailersActive
      }
      site {
        siteMetadata {
          title
          siteUrl
          description
          author
          lang
        }
      }
      labelManagerLabels: labelManagerLabels {
        price
        avaiableOn
        orderBy
        nameProductListOrd
        dateProductListOrd
        priority
        priorityPerPrice
        filterBy
        cancel
        readMore
        readLess
        show
        of
        results
        addToCart
        preorder
        whereToBuy
        showProduct
        compare
        removeFromComparison
        buyNow
        loadMore
        noResultsFound
        applyFilters
        priceAsc
        priceDesc
        category
        yes
        no
        close
        filters
        productCategory
        all
        buyOnHaierShop
        insertSkuProduct
        noOptionsSelect
        compatibleAppliance
        incompatibleAppliance
        compatibleWithAllAccessiories
        verifyCompitability
      }
    }
  `);

  /* const getCurrencySymbol = (currency = "EUR", lang = "it-IT") => {
    return Intl.NumberFormat(lang, { style: "currency", currency })
      .formatToParts(1)
      .find((item) => item.type === "currency")?.value;
  }; */

  const onToggleProduct = useCallback((e) => toggleProduct(e.currentTarget.name), [toggleProduct]);
  // Leggi di più
  const [more, setMore] = useState(false);
  const toggleMore = useCallback(() => setMore((value) => !value), []);

  // Filtri mobile visibili
  const [filtersMb, setFiltersMb] = useState(false);
  const openFiltersMb = useCallback(() => setFiltersMb(true), []);
  const closeFiltersMb = useCallback(() => setFiltersMb(false), []);
  // Blocca lo scroll quando i filtri mobile sono aperti
  useBlockScroll(filtersMb);
  const [showOrdering, setShowOrdering] = useState(false);

  // Prezzi prodotti magento
  const sliderProductIds = useMemo(() => allProducts?.map((item) => item.key_prod), [allProducts]);
  const priceInfo = useProductPrices(sliderProductIds, allProducts);

  const allSortOptions = useMemo(() => {
    const defaultSortOption = {
      priority: {
        label: labelManagerLabels.priority,
        value: "priority",
      },
      product_name: {
        label: labelManagerLabels.nameProductListOrd,
        value: "product_name",
      },
      date_rt: {
        label: labelManagerLabels.dateProductListOrd,
        value: "date_rt",
      },
      price_asc: {
        label: labelManagerLabels.priceAsc,
        value: "price_asc",
      },
      price_desc: {
        label: labelManagerLabels.priceDesc,
        value: "price_desc",
      },
      priority_per_price: {
        label: labelManagerLabels.priorityPerPrice,
        value: "priority_per_price",
      },
    };

    if (!order?.length) {
      return [...Object.values(defaultSortOption)];
    }

    return order?.map((sortType) => defaultSortOption[sortType])?.filter(Boolean) || [];
  }, [labelManagerLabels, order]);

  const sortOptions = useMemo(() => {
    return priceInfo?.size === 0 || !priceInfo
      ? allSortOptions.filter((item) => !PRICE_SORT_OPTIONS.includes(item.value))
      : allSortOptions;
  }, [allSortOptions, priceInfo]);

  // Query string
  const [query, updateQuery] = useKeepScroll(location);

  const { data: dataProduct, isLoading } = useProduct(true, query?.compatibleSku);

  const sortBy = sortOptions.find((option) => option.value === query.sort)
    ? query.sort
    : sortOptions?.[0]?.value;

  const sortByPrice =
    PRICE_SORT_OPTIONS.includes(allSortOptions?.[0]?.value) || PRICE_SORT_OPTIONS.includes(sortBy);

  const showLoader = ((filterMagento?.length || sortByPrice) && !priceInfo) || isLoading;

  const products = useMemo(() => {
    if (!filterMagento?.length && !sortByPrice) {
      return allProducts;
    }

    if (!priceInfo) {
      return [];
    }

    if (filterMagento.includes("priceSpecial")) {
      return allProducts.filter(
        (item) =>
          priceInfo.get(item.key_prod)?.sellingPrice?.value &&
          ((priceInfo.get(item.key_prod)?.omnibusPrice?.value &&
            priceInfo.get(item.key_prod)?.sellingPrice?.value <
              priceInfo.get(item.key_prod)?.omnibusPrice?.value) ||
            (!priceInfo.get(item.key_prod)?.omnibusPrice?.value &&
              priceInfo.get(item.key_prod)?.sellingPrice?.value <
                priceInfo.get(item.key_prod)?.msrpPrice?.value))
      );
    }

    if (filterMagento.includes("inStock")) {
      return allProducts.filter((item) => priceInfo.has(item.key_prod));
    }

    if (filterMagento.includes("inStock") && filterMagento.includes("priceSpecial")) {
      return allProducts.filter(
        (item) =>
          priceInfo.get(item.key_prod)?.sellingPrice?.value &&
          ((priceInfo.get(item.key_prod)?.omnibusPrice?.value &&
            priceInfo.get(item.key_prod)?.sellingPrice?.value <
              priceInfo.get(item.key_prod)?.omnibusPrice?.value) ||
            (!priceInfo.get(item.key_prod)?.omnibusPrice?.value &&
              priceInfo.get(item.key_prod)?.sellingPrice?.value <
                priceInfo.get(item.key_prod)?.msrpPrice?.value))
      );
    }

    return allProducts;
  }, [allProducts, filterMagento, priceInfo, sortByPrice]);

  const sortFunctions = useMemo(() => {
    const price_desc = (a, b) => {
      const aInStock = priceInfo.has(a.key_prod);
      const bInStock = priceInfo.has(b.key_prod);
      if (aInStock !== bInStock) {
        return aInStock ? -1 : 1;
      }
      return -only_price_asc(a, b);
    };

    const price_asc = (a, b) => {
      const aInStock = priceInfo.has(a.key_prod);
      const bInStock = priceInfo.has(b.key_prod);
      if (aInStock !== bInStock) {
        return aInStock ? -1 : 1;
      }

      return only_price_asc(a, b);
    };

    const only_price_asc = (a, b) => {
      const priceA = priceInfo.get(a.key_prod)?.sellingPrice?.value;
      const priceB = priceInfo.get(b.key_prod)?.sellingPrice?.value;
      return priceA === priceB ? 0 : priceA < priceB ? -1 : 1;
    };

    const priority_per_price = (a, b) => {
      const aInStock = priceInfo.has(a.key_prod);
      const bInStock = priceInfo.has(b.key_prod);
      const priorityA = a.productPriority ? parseInt(a.productPriority) : 0;
      const priorityB = b.productPriority ? parseInt(b.productPriority) : 0;
      if (aInStock !== bInStock) {
        return aInStock ? -1 : 1;
      }
      if (aInStock === bInStock) {
        return priorityA === priorityB ? 0 : priorityA > priorityB ? -1 : 1;
      }
    };

    return {
      product_name: (a, b) => (a.productName < b.productName ? -1 : 1),
      date_rt: (a, b) => (a.date_rt_ISO < b.date_rt_ISO ? 1 : -1),
      priority: (a, b) => {
        const aInStock = priceInfo?.has(a.key_prod);
        const bInStock = priceInfo?.has(b.key_prod);
        if (aInStock !== bInStock) {
          return aInStock ? -1 : 1;
        }

        const priorityA = a.productPriority ? parseInt(a.productPriority) : 0;
        const priorityB = b.productPriority ? parseInt(b.productPriority) : 0;

        if (priorityA !== priorityB) {
          return priorityA > priorityB ? -1 : 1;
        }

        if (!aInStock) {
          return 0;
        }

        const priceA = priceInfo.get(a.key_prod);
        const priceB = priceInfo.get(b.key_prod);
        return priceA === priceB ? 0 : priceA < priceB ? -1 : 1;
      },
      price_asc,
      price_desc,
      priority_per_price,
    };
  }, [priceInfo]);

  const allPageLines = useMemo(() => {
    const inserted = new Map();
    const pageLines = [];
    for (const product of products) {
      if (!inserted.has(product.pageLine.liferayFields.title)) {
        inserted.set(product.pageLine.liferayFields.title, true);
        pageLines.push(product.pageLine);
      }
    }
    return pageLines;
  }, [products]);

  // const isAccessoriConsumabili = allPageLines.every(
  //   (pageLine) => pageLine.productLine?.[0]?.value?.[0] === "333"
  // );
  const selectedCategory = query.category;
  // const queryCompatibility = isAccessoriConsumabili ? query.compatibility : null;

  // Faccette mostrate
  const facets = useMemo(() => {
    const getFilterValues = (products) => (facet) => ({
      ...facet,
      values:
        facet.id === "compatibility"
          ? facet.values
          : facet.values.filter((value) =>
              products.some((product) => {
                const itemAttr = product.attributesMap[facet.id];
                return (
                  itemAttr &&
                  (Array.isArray(itemAttr.fieldValue)
                    ? itemAttr.fieldValue.includes(value)
                    : `${itemAttr.fieldValue}` === value || itemAttr.fieldBoolean === value)
                );
              })
            ),
    });

    if (allPageLines?.length === 1) {
      const productsSelected = products.filter(
        (product) => product.pageLine.liferayFields.title === allPageLines[0].liferayFields.title
      );
      const firstLine = allPageLines[0].computedFilters;

      return firstLine

        .map(getFilterValues(productsSelected))
        .filter((facet) => facet.values.length > 1);
    }

    const selectedLine =
      selectedCategory && !Array.isArray(selectedCategory)
        ? allPageLines.find((item) => item.liferayFields.title === selectedCategory)
        : null;

    const categoryFacet = {
      id: "category",
      name: labelManagerLabels.category,
      type: "PILLS",
      values: allPageLines.map((pageLine) => pageLine.liferayFields.title).filter(Boolean),
      hashMap: labelManagerLabels.category,
    };

    if (selectedLine) {
      const productsSelected = products.filter(
        (product) => product.pageLine.liferayFields.title === selectedLine.liferayFields.title
      );

      return [categoryFacet].concat(
        selectedLine.computedFilters
          // .slice(0, 1)
          .map(getFilterValues(productsSelected))
          .filter((facet) => facet.values.length > 1)
      );
    } else {
      return [categoryFacet];
    }
  }, [labelManagerLabels, allPageLines, selectedCategory, products]);

  const currentFacets = useMemo(() => {
    const results = [];
    if (query.price) {
      results.push({
        id: "price",
        current: query.price,
        values: ["true", "false"],
      });
    }
    if (query.prices) {
      results.push({
        id: "prices",
        current: query.prices,
        values: priceInfo
          ? Object.values(priceInfo)
              .map((itemData) => {
                const price = itemData?.sellingPrice?.value;
                return price;
              })
              .filter(Boolean)
          : [],
      });
    }
    return results.concat(
      facets.map((facet) => ({
        ...facet,
        name: facet.id === "compatibility" ? labelManagerLabels.productCategory : facet.name,
        current: query[facet.id],
      }))
    );
  }, [query, facets, priceInfo, labelManagerLabels.productCategory]);

  // Clean query from facet parameters
  const clearFacets = useCallback(() => {
    const cleanedQuery = Object.fromEntries(
      Object.entries(query).filter(
        ([key]) => !["price", "prices"].includes(key) && !facets?.some((item) => item.id === key)
      )
    );
    updateQuery(cleanedQuery);
    closeFiltersMb();
    const event = new CustomEvent("ClearFilter", {
      detail: {},
    });
    document.dispatchEvent(event);
  }, [query, updateQuery, closeFiltersMb, facets]);
  const isSelectedFacet = currentFacets.some((item) => item.current !== undefined);

  // Valori validi faccette
  const facetOptions = useMemo(() => {
    return currentFacets.reduce((res, facet) => {
      res[facet.id] = ["category"].includes(facet.id)
        ? facet.values
        : extractValues(facet, products, currentFacets, query, priceInfo, dataProduct);
      return res;
    }, {});
  }, [currentFacets, products, query, priceInfo, dataProduct]);

  // Lista prodotti filtrata per faccette
  const productsFiltered = useMemo(() => {
    return currentFacets
      ? products.filter((product) =>
          productMatchesFilters(product, currentFacets, priceInfo, dataProduct)
        )
      : products;
  }, [products, currentFacets, priceInfo, dataProduct]);

  const selectFacet = useCallback(
    (e) => {
      const { name, checked } = e.target;
      const facetId = e.target.getAttribute("data-facet");
      const facet = facets.find((item) => item.id === facetId);
      const multiple = facetId === "category";
      if (name === false) {
        const newQuery = { ...query };
        delete newQuery[facetId];
        updateQuery(newQuery);
      } else if (multiple) {
        const currentValue = query[facetId];
        const currentArrayValue = currentValue
          ? Array.isArray(currentValue)
            ? currentValue
            : [currentValue]
          : [];
        const updatedValue = currentArrayValue.includes(name)
          ? currentArrayValue.filter((item) => item !== name)
          : currentArrayValue.concat(name);
        if (updatedValue.length) {
          const cleanQuery =
            updatedValue.length > 1
              ? Object.fromEntries(
                  Object.entries(query).filter(([key]) => ["price", "prices"].includes(key))
                )
              : query;
          updateQuery({
            ...cleanQuery,
            [facetId]: updatedValue.length > 1 ? updatedValue : updatedValue[0],
          });
        } else {
          const newQuery = { ...query };
          delete newQuery[facetId];
          updateQuery(newQuery);
        }
      } else {
        let result = name;
        if (checked !== undefined && query[facetId]) {
          if (Array.isArray(query[facetId])) {
            result = checked
              ? query[facetId].filter((e) => e !== name).concat(name)
              : query[facetId].filter((e) => e !== name);
          } else if (query[facetId] === name && !checked) {
            const newQuery = { ...query };
            delete newQuery[facetId];
            updateQuery(newQuery);
            return;
          } else {
            result = [query[facetId]];
            result = checked
              ? result.filter((e) => e !== name).concat(name)
              : result.filter((e) => e !== name);
          }
        }
        if (facet.type === "RANGE") {
          // Clear out range filter if given exactly min and max values
          const sorted = facet.values
            ? [...facet.values].sort((a, b) => Number(a) - Number(b))
            : facet.values;
          const min = sorted[0];
          const max = sorted[sorted.length - 1];
          const [start, end] = result;
          result = Number(start) === Number(min) && Number(end) === Number(max) ? null : result;
        }
        const newQuery = { ...query };
        // if (facet.id === "compatibility") {
        //   const allOtherFilters = Object.keys(newQuery).filter(
        //     (key) => !["category", "compatibility"].includes(key)
        //   );
        //   allOtherFilters.forEach((key) => {
        //     delete newQuery[key];
        //   });
        // }
        if (result === null) {
          delete newQuery[facetId];
          updateQuery(newQuery);
        } else {
          updateQuery({
            ...newQuery,
            [facetId]: result,
          });
        }
      }
    },
    [query, updateQuery, facets]
  );

  const clearFacet = useCallback(
    (e) => {
      const { name } = e.target;
      const newQuery = { ...query };
      delete newQuery[name];
      updateQuery(newQuery);
    },
    [query, updateQuery]
  );

  const filterPrice = useCallback(
    (value) => {
      if (value) {
        updateQuery({ ...query, price: true });
      } else {
        delete query.price;
        updateQuery(query);
      }
    },
    [query, updateQuery]
  );

  // Ordinamento
  const handleOrdering = () => {
    setShowOrdering(true);
  };

  const onChangeSort = useCallback(
    (e) => updateQuery({ ...query, sort: e.target.value }),
    [query, updateQuery]
  );
  const productsSorted = useMemo(() => {
    const sortedList = productsFiltered.slice(0);
    const sortFunc = sortFunctions[sortBy];
    return sortFunc ? sortedList.sort(sortFunc) : sortedList;
  }, [productsFiltered, sortBy, sortFunctions]);

  const isMobile = useIsMobile(991);
  // Paginazione
  const pagination = isMobile ? 15 : 30;
  const queryPage = parseInt(query.page);
  const page =
    isNaN(queryPage) || queryPage < 1 || queryPage > Math.ceil(productsSorted / 15) ? 1 : queryPage;
  const productsPaginated = useMemo(() => {
    const paginated = productsSorted.slice(0, page * pagination);
    return priceInfo
      ? paginated.map((item) => {
          const itemData = priceInfo.get(item.key_prod);
          return {
            ...item,
            msrpPrice: itemData?.msrpPrice || null,
            sellingPrice: itemData?.sellingPrice || null,
            omnibusPrice: itemData?.omnibusPrice || null,
            availableForSale: itemData?.availableForSale,
            shopLink: itemData?.shopLink || item?.shopLink || null,
            special_from_date: itemData?.special_from_date,
            special_to_date: itemData?.special_to_date,
            contributoRaee: itemData?.contributo_raee ? itemData.contributo_raee : null,
          };
        })
      : paginated;
  }, [productsSorted, page, priceInfo, pagination]);
  const canLoad = productsSorted.length > productsPaginated.length;
  const loadMore = useCallback(
    () => updateQuery({ ...query, page: page + 1 }),
    [query, page, updateQuery]
  );

  const jsonLdData = useMemo(() => {
    if (!productsPaginated?.length) return null;
    return {
      "@context": "http://schema.org/",
      "numberOfItems": productsPaginated.length,
      "@type": "ItemList",
      "itemListElement": productsPaginated.map((product, idx) => ({
        "position": idx + 1,
        "@type": "ListItem",
        "url": `${process.env.GATSBY_SITE_URL}${withPrefix(
          "/" +
            [
              product.pageLine.slug.startsWith("/")
                ? product.pageLine.slug.substring(1)
                : product.pageLine.slug,
              product.key_prod,
              product.productName.toLowerCase(),
            ]
              .map(safeSlufigy)
              .join("/")
        )}/`,
      })),

      "url": `${process.env.GATSBY_SITE_URL}${withPrefix(
        normalizeRelativePath(productsPaginated[0]?.pageLine?.slug)
      )}`,
    };
  }, [productsPaginated]);

  const [dataLayerPage, setDataLayerPage] = useState(0);
  useEffect(() => {
    if ((!hasEcommerce || priceInfo) && dataLayerPage < page) {
      const startIndex = dataLayerPage * 15;
      productImpression(productsPaginated.slice(startIndex), "Product listing page cross");
      setDataLayerPage(page);
    }
  }, [productsPaginated, page, priceInfo, dataLayerPage]);

  const allPrices = useMemo(() => {
    const allProductSkus = products.map((item) => item.key_prod);
    return !!priceInfo?.size
      ? [...priceInfo.values()]
          .filter((item) => allProductSkus.includes(item.sku))
          .map((item) => item.sellingPrice?.value)
          .sort((a, b) => a - b)
      : [];
  }, [priceInfo, products]);

  const pricesOptions = useMemo(() => {
    return !!priceInfo?.size
      ? productsFiltered
          .map((product) => priceInfo.get(product.key_prod))
          .filter(Boolean)
          .map((item) => item.sellingPrice?.value)
          .filter(Boolean)
          .sort((a, b) => a - b)
      : [];
  }, [priceInfo, productsFiltered]);

  const hasPrice = !!query.prices || query.price === "true";

  const selectPrice = ({ start, end }) => {
    const min = allPrices[0];
    const max = allPrices[allPrices.length - 1];
    if (Number(min) === Number(start) && Number(max) === Number(end)) {
      const newQuery = { ...query };
      delete newQuery.prices;
      updateQuery(newQuery);
    } else {
      updateQuery({ ...query, prices: [start, end] });
    }
  };
  const noRtl = useNoRtl();

  const next = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const currentPage = params.get("page") || 1;
    return productsSorted.length > productsPaginated.length ? Number(currentPage) + 1 : null;
  }, [location.search, productsSorted.length, productsPaginated.length]);

  const prev = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const currentPage = params.get("page") || 1;
    return currentPage > 1 ? Number(currentPage) - 1 : null;
  }, [location.search]);

  const isAllAccCompatible = useMemo(() => {
    return products.every(
      (value) =>
        dataProduct?.GetProductDetailsResult.accessoriesByLine
          .split(",")
          .map((item) => item.trim())
          .includes(value.key_prod) ||
        dataProduct?.GetProductDetailsResult.accessoriesBySku
          .split(",")
          .map((item) => item.trim())
          .includes(value.key_prod)
    );
  }, [products, dataProduct]);

  return (
    <div
      className={classNames(
        "product-list position-relative",
        hasHero ? `product-list--hero ${className}` : "product-list--nohero"
      )}
    >
      <Helmet>
        {next && (
          <link
            rel="next"
            href={`${site.siteMetadata.siteUrl}${withPrefix(
              normalizeRelativePath(pageCrossLine.slug)
            )}?page=${next}`}
          ></link>
        )}
        {!!prev && prev !== 1 ? (
          <link
            rel="prev"
            href={`${site.siteMetadata.siteUrl}${withPrefix(
              normalizeRelativePath(pageCrossLine.slug)
            )}?page=${prev}`}
          ></link>
        ) : !!prev && prev === 1 ? (
          <link
            rel="prev"
            href={`${site.siteMetadata.siteUrl}${withPrefix(
              normalizeRelativePath(pageCrossLine.slug)
            )}`}
          ></link>
        ) : null}
      </Helmet>
      <JsonLd data={jsonLdData} />
      <div className="container-fluid">
        {hasHero && !categoryModule && (
          <div className="row product-list__intro">
            <div className="col-lg-8 col-xl-9 offset-lg-4 offset-xl-3">
              <div className="text-center">
                {hasHero && (
                  <>
                    {pclTitle && <h1 className="product-list__title">{pclTitle}</h1>}
                    {pclIntroBefore && (
                      <div
                        className="product-list__description"
                        dangerouslySetInnerHTML={{ __html: pclIntroBefore }}
                      ></div>
                    )}
                    {pclIntroBefore && pclIntroAfter && (
                      <>
                        <div
                          className={`product-list__description ${more ? "d-block" : "d-none"}`}
                          dangerouslySetInnerHTML={{ __html: pclIntroAfter }}
                        ></div>
                        <button className="product-list__more" onClick={toggleMore}>
                          <span className={more ? "d-none" : "d-inline"}>
                            {labelManagerLabels.readMore}
                          </span>
                          <span className={more ? "d-inline" : "d-none"}>
                            {labelManagerLabels.readLess}
                          </span>
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        <div className="row">
          <div className="col-4 col-xl-3 px-0">
            <div
              className={classNames(`product-list__filters d-lg-flex`, { "d-none": !filtersMb })}
            >
              <div className="product-list__filters-content">
                {!!facets?.length && productsPaginated.length > 0 ? (
                  <div className="product-list__filters-intro pb-3 d-flex align-items-center justify-content-between">
                    <div className="product-list__filters-title d-none d-lg-block">
                      {labelManagerLabels.filterBy}
                    </div>
                    <button
                      className="product-list__filters-close-label d-lg-none"
                      onClick={closeFiltersMb}
                      aria-label="Close"
                    >
                      {labelManagerLabels.close}
                    </button>
                    <button
                      className={classNames("product-list__reset-btn ml-2", {
                        "d-none": !isSelectedFacet,
                      })}
                      onClick={clearFacets}
                    >
                      {labelManagerLabels.cancel}
                      <RotateIcon />
                    </button>
                  </div>
                ) : null}
                {products?.every((product) => product?.lineId === 333) && (
                  <>
                    <div className="product-list__facet-label">
                      <span className="product-list__filter-btn">
                        <span>{labelManagerLabels.verifyCompitability}</span>
                      </span>
                    </div>
                    <AutocompleteSelectPlp
                      labelManagerLabels={labelManagerLabels}
                      updateQuery={updateQuery}
                      query={query}
                      isAllAccCompatible={isAllAccCompatible}
                    />
                  </>
                )}
                {(pricesOptions.length > 0 || hasPrice) && !noRtl && (
                  <>
                    {(pricesOptions?.length < productsFiltered?.length || hasPrice) && !noRtl && (
                      <label
                        htmlFor="price-toggle"
                        className="d-flex mt-4 mb-3 justify-content-between align-items-center"
                      >
                        <span className="product-list__filter-btn mr-3">
                          {labelManagerLabels.avaiableOn}
                        </span>
                        <ToggleSwitch
                          id="price-toggle"
                          checked={hasPrice}
                          disabled={!!query.prices}
                          setChecked={(value) => {
                            filterPrice(value);
                            const event = new CustomEvent("FilterChange", {
                              detail: {
                                attributeId: "price-toggle",
                                attributeValue: value,
                                name: "Price Toggle",
                                type: "SWITCH",
                              },
                            });
                            document.dispatchEvent(event);
                          }}
                        />
                      </label>
                    )}
                    {(pricesOptions?.length > 1 || !!query.prices) && (
                      <div className="product-list__facet">
                        <div className="d-flex align-items-center">
                          <span className="product-list__filter-btn">
                            {labelManagerLabels.price}
                          </span>
                          <button
                            className={classNames(
                              "product-list__reset-btn product-list__reset-btn--single",
                              {
                                "d-none": !query.prices,
                              }
                            )}
                            name="prices"
                            onClick={clearFacet}
                          >
                            {labelManagerLabels.cancel}
                            <RotateIcon />
                          </button>
                        </div>
                        <DoubleRangeSlider
                          start={query.prices ? Number(query.prices[0]) : pricesOptions[0]}
                          end={
                            query.prices
                              ? Number(query.prices[query.prices.length - 1])
                              : pricesOptions[pricesOptions.length - 1]
                          }
                          values={allPrices}
                          unit={currency}
                          onChange={(values) => {
                            selectPrice(values);
                            const event = new CustomEvent("FilterChange", {
                              detail: {
                                attributeId: "prices",
                                attributeValue: [values.start, values.end],
                                name: "Price",
                                type: "RANGE",
                              },
                            });
                            document.dispatchEvent(event);
                          }}
                        />
                      </div>
                    )}
                  </>
                )}
                <div className="d-flex flex-column mt-4">
                  {currentFacets
                    ?.filter(
                      (facet) =>
                        (!["price", "prices"].includes(facet.id) && facet.id === "category") ||
                        facetOptions[facet.id].length > 1 ||
                        (facetOptions[facet.id]?.length === 1 && query[facet.id])
                    )
                    .map((facet) => (
                      <Facet
                        facet={facet}
                        facetOptions={facetOptions}
                        selectFacet={selectFacet}
                        clearFacet={clearFacet}
                        labelManagerLabels={labelManagerLabels}
                        multiple={facet.id !== "category" && facetOptions[facet.id].length > 2}
                        key={facet.id}
                      />
                    ))}
                </div>
                <div className="mt-3 d-flex justify-content-center d-lg-none">
                  <button className="product-list__mb-filter" onClick={closeFiltersMb}>
                    {labelManagerLabels.applyFilters}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-8 col-xl-9 px-0 pl-md-3">
            <div className="product-list__products">
              <div className="product-list__topbar pb-2">
                <div className="row mt-5">
                  <div
                    className={classNames(
                      "product-list__count-wrapper col-sm-6 d-none d-lg-block",
                      {
                        invisible: !productsPaginated.length,
                      }
                    )}
                  >
                    <div className="product-list__result-count text-center text-sm-left mb-4 mb-sm-0">
                      <span>
                        {labelManagerLabels.show} {productsPaginated.length}
                      </span>
                      <span className="text-lowercase">
                        {" "}
                        {labelManagerLabels.of} {productsSorted.length}
                      </span>
                      <span className="text-lowercase"> {labelManagerLabels.results}</span>
                    </div>
                  </div>
                  <div
                    className={classNames(
                      "col-sm-12 d-flex justify-content-between align-items-center pb-1 col-lg-6 d-lg-block",
                      { invisible: productsPaginated.length < 1 }
                    )}
                  >
                    {!!facets.length && (
                      <div className="text-center d-inline-block d-lg-none">
                        <button className="product-list__btn-filter" onClick={openFiltersMb}>
                          <Filter />
                          {labelManagerLabels.filters}
                          {Object.keys(query).filter((key) => key !== "sort").length > 0 && (
                            <div className="box-search__filter-icon"></div>
                          )}
                        </button>
                      </div>
                    )}
                    {productsPaginated.length >= 2 && (
                      <label
                        htmlFor="product-list-sort-by"
                        className="d-flex align-items-center justify-content-end mb-0"
                      >
                        <button
                          className={classNames("product-list__btn-filter d-lg-none")}
                          disabled={showOrdering}
                          onClick={handleOrdering}
                        >
                          {labelManagerLabels?.orderBy}
                          <Order className={classNames(showOrdering && "d-none")} />
                        </button>
                        <CustomSelect
                          id="product-list-sort-by"
                          className={classNames(
                            "product-list__sort-by flex-fill flex-md-grow-0 ml-3 d-lg-block",
                            !showOrdering && "d-none"
                          )}
                          options={sortOptions}
                          value={sortBy}
                          onChange={(e) => {
                            onChangeSort(e);
                            setShowOrdering(false);
                          }}
                        />
                      </label>
                    )}
                  </div>
                </div>
              </div>
              <div className="row py-4">
                {showLoader ? (
                  <div className="d-flex justify-content-center w-100 py-4">
                    <div className="spinner-border mr-2"></div>
                  </div>
                ) : productsPaginated.length ? (
                  productsPaginated.map((item, index) => (
                    <div
                      className="col-sm-6 col-xl-4 px-0 product-list__product-card"
                      key={item.key_prod}
                    >
                      <CardProdotto
                        data={item}
                        productIds={productIds}
                        toggleProduct={onToggleProduct}
                        addCart={true}
                        heading2={true}
                        config={config}
                        origin="Product listing page cross"
                        index={index}
                        labelManagerLabels={labelManagerLabels}
                        isList={true}
                        isSingleProdInList={productsPaginated.length === 1}
                        location={location}
                      />
                    </div>
                  ))
                ) : (
                  <div className="product-list__empty text-center py-4">
                    {labelManagerLabels.noResultsFound}
                  </div>
                )}
                {!!bannerPromoCM &&
                  bannerPromoCM.map(
                    (item, index) =>
                      index < 2 && (
                        <div
                          className={classNames(
                            "col-sm-6 col-xl-4 px-0",
                            index === 1
                              ? "product-list__banner-promo-second"
                              : "product-list__banner-promo"
                          )}
                        >
                          <BannerPushNotification sleekNoteId="sn-785b13d4-ee9e-4fc6-acfe-e7bcc50e61dd">
                            <BannerPromoCM data={item} position="plp" />
                          </BannerPushNotification>
                        </div>
                      )
                  )}
              </div>
              {!showLoader ? (
                <div className={`row py-4${canLoad ? "" : " d-none"}`}>
                  <div className="col-12 text-center">
                    <div className="product-list__size mb-3 text-lowercase">
                      {productsPaginated.length} {labelManagerLabels.of} {productsSorted.length}
                    </div>
                    {productsSorted.length > productsPaginated.length && (
                      <button className="btn-link button secondary" onClick={loadMore}>
                        {labelManagerLabels.loadMore}{" "}
                        {`(${Math.min(
                          pagination,
                          productsSorted.length - productsPaginated.length
                        )})`}
                      </button>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductListCross;
